import {useContext, useEffect, useMemo} from 'react'

import {useBranch} from '../../../../hooks/routes'
import {getChangesArg} from '../../../../rest/arguments'
import {getOverviewHref} from '../../../../routes'
import {restApi} from '../../../../services/rest'
import {BuildTypePageTabNamesEnum} from '../../../../types'
import {internalProps} from '../../../../types/BS_types'
import {stringifyBranch} from '../../../../utils/branchNames'
import {SequenceLoaderContext} from '../../../common/SequenceLoader/SequenceLoader.hooks'
import {ChangeContext, ChangeView, DEFAULT_CHANGES_LIST_CONTEXT} from '../Change/Change.context'
import {getChangesLocator, getPendingChangesBaseLocator} from '../Changes.utils'

import ChangesPreview from './ChangesPreview'
import type {ContainerProps} from './ChangesPreview.types'

const CHANGES_LIMIT = Number(internalProps['teamcity.internal.buildChangesPopupLimit'])
export default function PendingChangesPreview(props: ContainerProps) {
  const {buildTypeId, changesCount, withChangesActions, withExpandableLines} = props

  const queryParamBranch = useBranch()
  const branch = props.branch ?? queryParamBranch

  const locator = getChangesLocator({
    baseLocator: getPendingChangesBaseLocator(buildTypeId, branch),
    count: CHANGES_LIMIT,
    lookupLimit: CHANGES_LIMIT,
  })

  const branchNameInHref = encodeURIComponent(stringifyBranch(branch))
  const baseChangesTabLink = `${getOverviewHref({
    buildTypeId,
  })}?buildTypeTab=${BuildTypePageTabNamesEnum.PENDING_CHANGES}&branch=${branchNameInHref}`

  const changesContextValue = useMemo(
    () => ({
      ...DEFAULT_CHANGES_LIST_CONTEXT,
      changeView: ChangeView.PREVIEW,
      withGraph: false,
      withChangesActions,
      withExpandableLines,
    }),
    [withChangesActions, withExpandableLines],
  )
  const {showFiles} = changesContextValue

  const [fetchChanges, {count, ready}] = restApi.endpoints.getAllChanges.useLazyQuery({
    selectFromResult: ({data}) => ({
      count: data?.changes?.result?.length ?? 0,
      ready: data != null,
    }),
  })
  const {onReady} = useContext(SequenceLoaderContext)

  useEffect(() => {
    fetchChanges(getChangesArg(locator, {withFiles: showFiles}))
  }, [changesCount, locator, fetchChanges, showFiles])

  useEffect(() => {
    if (onReady != null && ready && count === 0) {
      onReady()
    }
  }, [count, ready, onReady])

  return ready ? (
    <ChangeContext.Provider value={changesContextValue}>
      <ChangesPreview
        {...props}
        locator={locator}
        baseChangesTabLink={baseChangesTabLink}
        changesCount={count}
      />
    </ChangeContext.Provider>
  ) : null
}
